import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ROLES = [
  { key: "Ushers", label: "Ushers & Wayfinders" },
  { key: "Ticket Scanners", label: "Gate Scanners" },
  { key: "VIP Hospitality", label: "VIP / Hospitality" },
  { key: "Concessions", label: "Concessions & Bar" },
  { key: "Crowd Safety", label: "Crowd Safety" },
  { key: "Stagehands", label: "Stagehands & Teardown" },
  { key: "Retail Brand Ambassador", label: "Retail Brand Ambassador / Fashion" },
];

const EVENT_TYPES = [
  { value: "concert", label: "Concert / Arena Show" },
  { value: "sports", label: "Stadium Sport Game" },
  { value: "festival", label: "Festival" },
  { value: "gala", label: "VIP Gala / Corporate" },
  { value: "retail", label: "Retail Brand Ambassador / Fashion" },
];

const slug = (r) => r.toLowerCase().replace(/[^a-z]+/g, "-");

export default function CrewEstimator({ onApply }) {
  const [eventType, setEventType] = useState("concert");
  const [eventName, setEventName] = useState("");
  const [counts, setCounts] = useState({});

  const setQty = (key, raw) => {
    const n = Math.max(0, Math.min(999, parseInt(raw, 10) || 0));
    setCounts((c) => ({ ...c, [key]: n }));
  };

  const lines = ROLES.map((r) => ({ ...r, qty: counts[r.key] || 0 }));
  const selected = lines.filter((l) => l.qty > 0);
  const totalCrew = selected.reduce((s, l) => s + l.qty, 0);

  const bucket =
    totalCrew <= 15 ? "5-15" : totalCrew <= 50 ? "16-50" : totalCrew <= 100 ? "51-100" : "100+";

  const apply = () =>
    onApply({
      eventType,
      eventName: eventName.trim(),
      staffCount: bucket,
      roles: selected.map((l) => l.key),
      crewSplit: Object.fromEntries(selected.map((l) => [l.key, l.qty])),
    });

  return (
    <section id="estimator" className="py-20 sm:py-28 bg-[#0F111A] border-y border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xs uppercase tracking-[0.2em] font-mono-tactical font-semibold text-sky-400">
              Build Your Crew
            </p>
            <h2 className="font-display mt-3 text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-white">
              Tell Us Who You Need.
            </h2>
            <p className="mt-4 text-base text-slate-400 leading-relaxed">
              Enter the headcount for each role. Send it through as an enquiry
              and we'll come back to you with a tailored quote — usually within
              2 business hours.
            </p>

            <div className="mt-8">
              <label
                htmlFor="estimator-event-name"
                className="text-sm font-semibold text-slate-300 mb-2 block"
              >
                Event Name
              </label>
              <input
                id="estimator-event-name"
                data-testid="estimator-event-name-input"
                type="text"
                placeholder="e.g. Summer Sounds Festival 2026"
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                className="w-full bg-[#131622] border border-white/10 focus:border-sky-500 focus:outline-none rounded-sm px-3 py-2.5 text-sm text-white placeholder:text-slate-600"
              />
            </div>

            <div className="mt-6">
              <label className="text-sm font-semibold text-slate-300 mb-2 block">
                Event Type
              </label>
              <Select value={eventType} onValueChange={setEventType}>
                <SelectTrigger
                  data-testid="estimator-event-type-select"
                  className="bg-[#131622] border-white/10 text-white rounded-sm"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#131622] border-white/10 text-white">
                  {EVENT_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {lines.map((l) => (
                <div
                  key={l.key}
                  className={`flex items-center justify-between gap-4 rounded-sm border px-4 py-3 transition-colors duration-200 ${
                    l.qty > 0 ? "border-sky-500/50 bg-sky-500/5" : "border-white/10 bg-[#131622]"
                  }`}
                >
                  <label
                    htmlFor={`qty-${slug(l.key)}`}
                    className="text-sm font-semibold text-slate-300"
                  >
                    {l.label}
                  </label>
                  <input
                    id={`qty-${slug(l.key)}`}
                    data-testid={`estimator-role-input-${slug(l.key)}`}
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={999}
                    placeholder="0"
                    value={l.qty === 0 ? "" : l.qty}
                    onChange={(e) => setQty(l.key, e.target.value)}
                    className="w-20 bg-[#0F111A] border border-white/10 focus:border-sky-500 focus:outline-none rounded-sm px-3 py-2 text-right font-mono-tactical font-semibold text-white placeholder:text-slate-600"
                  />
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="bg-[#131622] border border-white/10 rounded-md p-7 lg:sticky lg:top-24"
          >
            <p className="font-mono-tactical text-[11px] uppercase tracking-widest text-slate-500">
              Your Crew — {EVENT_TYPES.find((t) => t.value === eventType)?.label}
            </p>
            {eventName.trim() && (
              <p
                data-testid="estimator-summary-event-name"
                className="mt-2 font-display text-lg font-bold uppercase text-white truncate"
              >
                {eventName}
              </p>
            )}
            <div className="mt-5 space-y-3 min-h-[60px]">
              {selected.length === 0 ? (
                <p className="text-sm text-slate-500">
                  Enter a number for any role to start building your crew.
                </p>
              ) : (
                selected.map((l) => (
                  <div
                    key={l.key}
                    data-testid={`estimator-line-${slug(l.key)}`}
                    className="flex items-center justify-between border-b border-white/5 pb-3"
                  >
                    <span className="text-sm text-slate-300">{l.label}</span>
                    <span className="font-mono-tactical font-semibold text-white">
                      × {l.qty}
                    </span>
                  </div>
                ))
              )}
            </div>

            <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between">
              <span className="font-display text-lg font-bold uppercase text-white">
                Total Crew
              </span>
              <span
                data-testid="estimator-total-crew"
                className="font-mono-tactical text-3xl font-bold text-sky-500"
              >
                {totalCrew}
              </span>
            </div>
            <p className="mt-3 text-xs text-slate-500">
              No pricing shown here — send your enquiry and we'll prepare a
              tailored quote for your event.
            </p>

            <button
              data-testid="estimator-apply-to-form-btn"
              onClick={apply}
              disabled={totalCrew === 0}
              className="group mt-6 w-full inline-flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 disabled:opacity-40 disabled:cursor-not-allowed text-[#090A0F] font-bold rounded-sm px-6 py-3.5 transition-all duration-200 hover:gap-3"
            >
              Send Enquiry for a Quote
              <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
